import { fetchConfig, debounce } from '@theme/utilities';
import { CartAddEvent, DiscountUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {object} AutoGwpWindowConfig
 * @property {boolean} enabled
 * @property {number} variantId
 * @property {number} thresholdCents
 * @property {string | null} discountCode
 * @property {boolean} [useDiscountedSubtotalForThreshold]
 * @property {boolean} lockGiftVariant
 */

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function parseVariantId(value) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return Math.trunc(value);
  const s = String(value ?? '').trim();
  if (!s) return null;
  const n = parseInt(s, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** @param {object} item */
function hasAutoGwpTag(item) {
  return Boolean(item.properties && String(item.properties._auto_gwp) === 'true');
}

/** @returns {AutoGwpWindowConfig | null} */
function readConfig() {
  const raw = window.__AUTO_GWP__;
  if (!raw || !raw.enabled) return null;
  const variantId = parseVariantId(raw.variantId);
  if (!variantId) return null;
  const code = raw.discountCode;
  const useDisc = raw.useDiscountedSubtotalForThreshold;
  return {
    enabled: raw.enabled,
    variantId,
    thresholdCents: Number(raw.thresholdCents) || 0,
    discountCode: code && String(code).trim() ? String(code).trim() : null,
    // Default on when not set in theme JSON: matches checkout after line discounts (bundles, etc.).
    // Theme can still pass useDiscountedSubtotalForThreshold: false to use catalog line totals instead.
    useDiscountedSubtotalForThreshold: useDisc == null ? true : Boolean(useDisc),
    lockGiftVariant: raw.lockGiftVariant !== false,
  };
}

/** @returns {string[]} */
function cartItemsSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

/**
 * Spend toward the minimum. When lockGiftVariant is on (recommended for a $0
 * dedicated SKU), every line of that variant is excluded so a free manual add
 * does not affect the math.
 *
 * @param {object} cart
 * @param {boolean} useDiscounted
 * @param {number} giftVariantId
 * @param {boolean} lockGiftVariant
 * @returns {number}
 */
function qualifyingSubtotalCents(cart, useDiscounted, giftVariantId, lockGiftVariant) {
  if (!cart.items?.length) return 0;
  return cart.items
    .filter((item) => {
      if (lockGiftVariant && Number(item.variant_id) === giftVariantId) return false;
      if (!lockGiftVariant && hasAutoGwpTag(item)) return false;
      return true;
    })
    .reduce((sum, item) => {
      if (useDiscounted) return sum + (Number(item.final_line_price) || 0);
      const original = Number(item.original_line_price);
      if (Number.isFinite(original) && original > 0) return sum + original;
      const linePrice = Number(item.line_price);
      if (Number.isFinite(linePrice) && linePrice > 0) return sum + linePrice;
      const unit = Number(item.price) * Number(item.quantity);
      if (Number.isFinite(unit) && unit > 0) return sum + unit;
      return sum + (Number(item.final_line_price) || 0);
    }, 0);
}

/**
 * @param {object} cart
 * @param {number} variantId
 * @returns {{ line: number; quantity: number; item: object }[]}
 */
function linesMatchingVariant(cart, variantId) {
  if (!cart.items?.length) return [];
  /** @type {{ line: number; quantity: number; item: object }[]} */
  const out = [];
  for (let i = 0; i < cart.items.length; i++) {
    const item = cart.items[i];
    if (Number(item.variant_id) === variantId) {
      out.push({ line: i + 1, quantity: Number(item.quantity) || 0, item });
    }
  }
  return out;
}

/**
 * @param {object} cart
 * @param {number} variantId
 * @returns {{ line: number; quantity: number } | null}
 */
function findAutoGwpLine(cart, variantId) {
  if (!cart.items?.length) return null;
  for (let i = 0; i < cart.items.length; i++) {
    const item = cart.items[i];
    if (Number(item.variant_id) === variantId && hasAutoGwpTag(item)) {
      return { line: i + 1, quantity: Number(item.quantity) || 0 };
    }
  }
  return null;
}

/**
 * @param {object} cart
 * @param {string | null} code
 * @returns {Promise<object | null>}
 */
async function mergeDiscountIfNeeded(cart, code) {
  if (!code || !String(code).trim()) return null;
  const sectionIds = cartItemsSectionIds();
  if (!sectionIds.length) return null;

  const target = String(code).trim();
  const existing = (cart.discount_codes || [])
    .filter((d) => d.applicable)
    .map((d) => d.code)
    .filter(Boolean);
  if (existing.some((c) => c.toUpperCase() === target.toUpperCase())) return null;

  const response = await fetch(Theme.routes.cart_update_url, {
    ...fetchConfig('json', {
      body: JSON.stringify({
        discount: [...existing, target].join(','),
        sections: sectionIds,
        sections_url: window.location.pathname,
      }),
    }),
  });
  if (!response.ok) return null;
  return response.json();
}

/** @returns {Promise<any | null>} */
async function fetchCartJson() {
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
  if (!res.ok) return null;
  return res.json();
}

/**
 * @param {Record<string, unknown>} body
 * @returns {Promise<any | null>}
 */
async function postCartChange(body) {
  const changeRes = await fetch(`${Theme.routes.cart_change_url}`, {
    ...fetchConfig('json', { body: JSON.stringify(body) }),
  });
  if (!changeRes.ok) return null;
  const text = await changeRes.text();
  return JSON.parse(text);
}

/**
 * @param {number} variantId
 * @param {Record<string, string>} sectionsPayload
 * @returns {Promise<void>}
 */
async function removeAllLinesForVariant(variantId, sectionsPayload) {
  /** @type {Record<string, string> | null} */
  let lastSections = null;
  for (let guard = 0; guard < 24; guard++) {
    const cart = await fetchCartJson();
    if (!cart?.items?.length) break;
    const matches = linesMatchingVariant(cart, variantId);
    if (matches.length === 0) break;
    matches.sort((a, b) => b.line - a.line);
    const targetLine = matches[0].line;
    const changeData = await postCartChange({
      line: targetLine,
      quantity: 0,
      ...sectionsPayload,
    });
    if (!changeData || changeData.errors) {
      console.warn('[auto-gwp] cart/change remove failed', changeData?.errors);
      break;
    }
    if (changeData.sections) lastSections = changeData.sections;
  }
  if (lastSections) publishCartUpdate(lastSections);
}

/**
 * @param {Record<string, string>} sections
 */
function publishCartUpdate(sections) {
  const cartEl = document.querySelector('cart-items-component');
  const sectionId = cartEl?.dataset?.sectionId;
  const sourceId = cartEl?.id || sectionId || 'auto-gwp';
  document.dispatchEvent(
    new CartAddEvent({}, sourceId, {
      source: 'auto-gwp',
      sections,
    })
  );
}

/**
 * @param {Record<string, string>} sections
 */
function publishDiscountUpdate() {
  const cartEl = document.querySelector('cart-items-component');
  const sectionId = cartEl?.dataset?.sectionId;
  const sourceId = cartEl?.id || sectionId || 'auto-gwp';
  document.dispatchEvent(new DiscountUpdateEvent({}, sourceId));
}

/**
 * @param {number} variantId
 * @param {Record<string, string>} sectionsPayload
 * @returns {Promise<boolean>}
 */
async function addGiftLine(variantId, sectionsPayload) {
  const body = {
    items: [{ id: variantId, quantity: 1, properties: { _auto_gwp: 'true' } }],
    ...sectionsPayload,
  };
  const addRes = await fetch(Theme.routes.cart_add_url, {
    ...fetchConfig('json', { body: JSON.stringify(body) }),
  });
  if (!addRes.ok) return false;
  const addData = await addRes.json();
  if (addData.status) {
    console.warn('[auto-gwp] cart/add failed', addData.message || addData.description || addData);
    return false;
  }
  if (addData.sections) publishCartUpdate(addData.sections);
  return true;
}

let syncing = false;
let resyncQueued = false;

async function syncAutoGwpBody() {
  const config = readConfig();
  if (!config) return;

  const cart = await fetchCartJson();
  if (!cart) return;
  const variantId = config.variantId;
  if (!variantId) return;

  const lock = config.lockGiftVariant;
  const threshold = Math.max(0, Number(config.thresholdCents) || 0);
  const subtotal = qualifyingSubtotalCents(
    cart,
    config.useDiscountedSubtotalForThreshold,
    variantId,
    lock
  );
  const qualifies = subtotal >= threshold;

  const sectionIds = cartItemsSectionIds();
  const sectionsPayload =
    sectionIds.length > 0
      ? {
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }
      : {};

  const giftLines = linesMatchingVariant(cart, variantId);

  if (lock) {
    if (!qualifies) {
      if (giftLines.length) await removeAllLinesForVariant(variantId, sectionsPayload);
      return;
    }

    const taggedOk =
      giftLines.length === 1 &&
      giftLines[0].quantity === 1 &&
      hasAutoGwpTag(giftLines[0].item);
    if (taggedOk) return;

    await removeAllLinesForVariant(variantId, sectionsPayload);
    const ok = await addGiftLine(variantId, sectionsPayload);
    if (ok && config.discountCode) {
      const cart2 = await fetchCartJson();
      if (cart2) {
        const discountData = await mergeDiscountIfNeeded(cart2, config.discountCode);
        if (discountData) publishDiscountUpdate();
      }
    }
    return;
  }

  const gwpLine = findAutoGwpLine(cart, variantId);

  if (qualifies && !gwpLine) {
    const ok = await addGiftLine(variantId, sectionsPayload);
    if (ok && config.discountCode) {
      const cart2 = await fetchCartJson();
      if (cart2) {
        const discountData = await mergeDiscountIfNeeded(cart2, config.discountCode);
        if (discountData) publishDiscountUpdate();
      }
    }
    return;
  }

  if (!qualifies && gwpLine) {
    const changeData = await postCartChange({
      line: gwpLine.line,
      quantity: 0,
      ...sectionsPayload,
    });
    if (changeData?.errors) return;
    if (changeData?.sections) publishCartUpdate(changeData.sections);
    return;
  }

  if (qualifies && gwpLine && gwpLine.quantity > 1) {
    const changeData = await postCartChange({
      line: gwpLine.line,
      quantity: 1,
      ...sectionsPayload,
    });
    if (changeData?.errors) return;
    if (changeData?.sections) publishCartUpdate(changeData.sections);
  }
}

async function syncAutoGwp() {
  if (syncing) {
    resyncQueued = true;
    return;
  }
  syncing = true;
  resyncQueued = false;
  try {
    await syncAutoGwpBody();
  } catch (e) {
    console.error('auto-gwp', e);
  } finally {
    syncing = false;
    if (resyncQueued) {
      resyncQueued = false;
      void syncAutoGwp();
    }
  }
}

const debouncedSync = debounce(() => {
  void syncAutoGwp();
}, 60);

function onCartOrDiscountEvent(/** @type {Event} */ event) {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.data?.source === 'auto-gwp') return;
  debouncedSync();
}

function init() {
  if (!readConfig()) return;
  debouncedSync();
  document.addEventListener(ThemeEvents.cartUpdate, onCartOrDiscountEvent);
  document.addEventListener(ThemeEvents.discountUpdate, onCartOrDiscountEvent);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
